import * as api from "./adapter";

export default {
  /**
   * getCrewUsers
   * @returns {Promise<*>}
   */
  index: ({
    pagination,
    sort,
    filter: { role, manager, owner, vessel, published, archived, assigned_to_me, overdue },
    search,
    pinnedIds,
  }) => {
    const filter = {};

    if (role) {
      const roleValues = Array.isArray(role) ? role.map(r => r.value) : [role.value];
      filter.role_id = roleValues;
    }
    if (manager) {
      const managerValues = Array.isArray(manager) ? manager.map(m => m.value) : [manager.value];
      filter.manager_id = managerValues;
    }
    if (owner) {
      const ownerValues = Array.isArray(owner) ? owner.map(o => o.value) : [owner.value];
      filter.owner_id = ownerValues;
    }
    if (vessel) {
      const vesselValues = Array.isArray(vessel) ? vessel.map(v => v.value) : [vessel.value];
      filter.vessel_id = vesselValues;
    }
    if (published) filter.published = published.value;
    // CREW-1230 — only the "on" state travels. The backend reads this in
    // VacancyResource.records to replace the open-slots scope, and `false` is
    // the default view, so sending it would only make the URL longer.
    if (archived && archived.value) filter.archived = true;
    // CREW-1169 "My vacancies": a flag, so only the "on" state is sent —
    // VacancyResource has no query for "taken by somebody else".
    if (assigned_to_me && assigned_to_me.value) filter.assigned_to_me = true;
    // CREW-1233 — same shape: the flag travels only when it is on.
    if (overdue && overdue.value) filter.overdue = true;
    if (search) filter.search = search;
    // Rows to show regardless of the "open positions only" scope — what the
    // recruiter just approved or published. Held in redux, not the URL, so a
    // reload drops them.
    if (pinnedIds && pinnedIds.length) filter.pinned = pinnedIds;

    return api.read("vacancies", {
      query: {
        filter,
        sort: `${sort.direction === "asc" ? "" : "-"}${sort.field}`,
        page: { size: pagination.itemsPerPage, number: pagination.current },
      },
    });
  },
  /**
   * show
   * @param id
   * @return {Promise}
   */
  show: (id) => api.read(`/vacancies/${id}`),
  /**
   * createCrewUser
   * @param attributes
   * @returns {Promise}
   */
  create: (attributes) => api.create({ type: "vacancies", attributes }),
  /**
   * updateCrewUser
   * @param id
   * @param attributes
   * @returns {Promise}
   */
  update: (id, attributes) => api.update({ id, type: "vacancies", attributes }),
  /**
   * deleteCrewUser
   * @returns {Promise<*>}
   */
  delete: (id) => api.destroy({ id, type: "vacancies" }),
  /**
   * Take the vacancy for the signed-in user (CREW-1167).
   * @param {number|string} vacancyId
   * @returns {Promise<{assignees: Array}>} the resulting assignee list
   */
  assign: async (vacancyId) => {
    const { data } = await api.apiRequest(`/api/vacancies/${vacancyId}/assignment`, {
      method: "POST",
    });

    return data;
  },
  /**
   * Release the vacancy. Without `clientId` people release themselves; with it
   * a manager takes somebody else off to redistribute the vacancy.
   * @param {number|string} vacancyId
   * @param {string} [clientId]
   * @returns {Promise<{assignees: Array}>} the resulting assignee list
   */
  unassign: async (vacancyId, clientId) => {
    const query = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
    const { data } = await api.apiRequest(`/api/vacancies/${vacancyId}/assignment${query}`, {
      method: "DELETE",
    });

    return data;
  },
  /**
   * republish
   * @returns {Promise<*>}
   */
  republish: async ({ vacancyId, source, cb }) => {
    const { data } = await api.apiRequest("/api/vacancies/republish", {
      method: "POST",
      body: JSON.stringify({ vacancyId, source }),
    });

    return cb(data);
  },
};
